<template>
  <div
    id="quick-task-content-edit"
    class="mx-auto grow flex flex-col"
    :class="isTaskUiFullscreen ? 'max-w-5xl' : 'max-w-3xl'"
  >
    <SingleQuickTaskResultWrapper
      data-flip-key="content"
      :icon-url="activeTaskIconUrl"
      title="Edit Content"
      :description="`(${taskConfig?.label})`"
      hide-quick-actions
      class="grow flex flex-col"
    >
      <template
        v-if="isSidebarSupported && (!isTaskUiFullscreen || isMobile)"
        #headingRight
      >
        <AppButton icon="pi pi-sparkles" variant="secondary" @click="showOverlay" />
      </template>
      <div class="sm:p-4 grow grid">
        <div class="h-full">
          <div v-if="isLoading || isError || !data || !taskConfig?.isContentEditable">
            <AppLoading v-if="isLoading || isLoadingConfig" wrapper-class="max-h-96" />
            <AppError v-else-if="isError" :error="error?.message" class="max-h-96" />
            <AppNoContent
              v-else-if="!taskConfig?.isContentEditable"
              :description="$t('contentNotEditable')"
              :button-text="$t('buttons.close')"
              class="max-h-96"
              @button-action="handleCancel"
            />
            <AppNoContent v-else :description="$t('taskNoContent')" />
          </div>

          <template v-else>
            <Transition
              enter-active-class="duration-300"
              leave-active-class="duration-75"
              enter-from-class="opacity-0 ease-in"
              leave-to-class="opacity-0 ease-out"
              mode="out-in"
            >
              <ContentEditCompare
                v-if="alternateContent"
                :original="data"
                :alternate="alternateContent"
                :data-path="dataPath"
                @accept="acceptContent"
                @reject="rejectContent"
              />
              <div
                v-else
                class="min-h-full grid duration-300"
                :class="
                  isTaskUiFullscreen && isSidebarSupported && !isMobile
                    ? 'md:grid-cols-[1fr_1rem] lg:grid-cols-[1fr_20rem]'
                    : ''
                "
              >
                <div class="w-full relative duration-300">
                  <ContentEditInputSection
                    :initial-data="content"
                    :content-type="contentType"
                    :images="images"
                    :videos="videos"
                    :loading="isUpdating"
                    :task-type="data?.taskType"
                    :max-number-of-images="taskConfig?.maxNumberOfImages"
                    :max-number-of-videos="taskConfig?.maxNumberOfVideos"
                    class="sm:px-4"
                    @remove-image="removeImage"
                    @remove-video="removeVideo"
                    @close="handleCancel"
                    @save="saveContent"
                  />
                </div>
                <div
                  v-if="isSidebarSupported && isTaskUiFullscreen && !isMobile"
                  class="w-full max-w-xs shrink-0 grid pb-4 pt-2 md:pt-0 absolute md:static bg-surface-0 md:ml-0 dark:bg-surface-800 top-16 right-1 bottom-1 md:border-l dark:border-surface-700 rounded-md md:rounded-none"
                >
                  <ContentEditActions
                    :images="images"
                    :videos="videos"
                    :can-add-image="taskConfig.canAddImages"
                    :can-add-video="taskConfig.canAddVideos"
                    :can-regenerate="taskConfig.isContentRegenerate"
                    :tone="taskConfig.tone"
                    :max-images="taskConfig.maxNumberOfImages"
                    :max-videos="taskConfig.maxNumberOfVideos"
                    :current-image-count="images.length"
                    :current-video-count="videos.length"
                    @image-selected="addImage"
                    @video-selected="addVideo"
                    @compare="alternateContent = $event"
                  />
                </div>
              </div>
            </Transition>
          </template>
        </div>
      </div>
    </SingleQuickTaskResultWrapper>

    <OverlayPanel
      v-if="
        taskConfig?.canAddImages ||
        taskConfig?.canAddVideos ||
        taskConfig?.isContentRegenerate
      "
      ref="overlay"
      :dismissable="false"
      :close-on-escape="false"
      :show-close-icon="false"
    >
      <div class="h-[80vh] max-h-137.5 w-screen max-w-xs relative">
        <ContentEditActions
          :images="images"
          :videos="videos"
          :can-add-image="taskConfig.canAddImages"
          :can-add-video="taskConfig.canAddVideos"
          :can-regenerate="taskConfig.isContentRegenerate"
          :tone="taskConfig.tone"
          :max-images="taskConfig.maxNumberOfImages"
          :max-videos="taskConfig.maxNumberOfVideos"
          :current-image-count="images.length"
          :current-video-count="videos.length"
          class="h-full"
          @image-selected="addImage"
          @video-selected="addVideo"
          @compare="alternateContent = $event"
        />
        <span class="absolute -top-3 -right-3 z-10">
          <AppButton icon="pi pi-times" variant="secondary" @click="cancelActions" />
        </span>
      </div>
    </OverlayPanel>
  </div>
</template>

<script setup lang="ts">
import AppNoContent from "@/components/shared/AppNoContent.vue";
import SingleQuickTaskResultWrapper from "./SingleQuickTaskResultWrapper.vue";
import AppError from "@/components/shared/AppError.vue";
import AppLoading from "@/components/shared/AppLoading.vue";
import ContentEditCompare from "@/components/content/edit/ContentEditCompare.vue";
import ContentEditInputSection from "@/components/content/edit/ContentEditInputSection.vue";
import ContentEditActions from "@/components/content/edit/ContentEditActions.vue";
import OverlayPanel from "@/volt/Popover.vue";

const props = defineProps<{
  beforeChange?: () => void;
  afterChange?: () => void;
}>();

const overlay = ref<InstanceType<typeof OverlayPanel> | null>(null);
const { contentEditData, isTaskUiFullscreen } = useQuickTask();
const { getTypeByName, getTaskIcon, taskFallbackIcon } = useTaskConfigDataCore();

const contentId = computed(() => {
  return contentEditData.value?.contentId || "";
});
const dataPath = computed(() => {
  return contentEditData.value?.dataPath || "";
});
const contentType = computed(() => {
  return contentEditData.value?.contentType || "plain-text";
});

const {
  addImage,
  addVideo,
  removeImage,
  removeVideo,
  acceptContent,
  alternateContent,
  content,
  isLoading,
  isUpdating,
  images,
  videos,
  isError,
  data,
  error,
  isSidebarSupported,
  rejectContent,
  taskConfig,
  isLoadingConfig,
  saveContent,
} = useContentEdit({
  handleCancel,
  contentId,
  dataPath,
  contentType,
});

const activeTaskTypeConfig = computed(() => {
  if (!taskConfig.value) return null;
  const [namespace, category] = taskConfig.value.namespace.split("/");
  return getTypeByName(category || namespace);
});

const activeTaskIconUrl = computed(() => {
  if (!taskConfig.value) return taskFallbackIcon;
  return (activeTaskTypeConfig.value || taskConfig.value) &&
    activeTaskTypeConfig.value?.namespace === "social-media"
    ? getTaskIcon(activeTaskTypeConfig.value.key, activeTaskTypeConfig.value.icon)
    : getTaskIcon(taskConfig.value?.key || "", taskConfig.value?.icon || "");
});

function showOverlay(event: MouseEvent) {
  overlay.value?.show(event, null);
}

function cancelActions() {
  overlay.value?.hide();
}

function handleCancel() {
  // Handle cancel action
  if (props.beforeChange) props.beforeChange();
  contentEditData.value = null;
  nextTick(() => {
    if (props.afterChange) props.afterChange();
  });
}
</script>
